import { AUTH_API_URL, API_URL } from '../config';
import {
  getAccessToken, setAccessToken,
  getRefreshToken, setRefreshToken, clearTokens,
} from './tokenStore';

function post(path, body) {
  return fetch(AUTH_API_URL + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
}

// Attaches the access token; on a 401 it refreshes once and retries.
export async function authedFetch(url, options = {}) {
  const run = () =>
    fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: 'Bearer ' + getAccessToken() },
    });
  let res = await run();
  if (res.status === 401 && (await refresh())) {
    res = await run();
  }
  return res;
}

export async function login(email, password) {
  const res = await post('/auth/login', { email, password });
  if (!res.ok) throw new Error('invalid_credentials');
  const data = await res.json();
  setAccessToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  return data.user;
}

export async function register(payload) {
  const res = await post('/auth/register', payload);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'register_failed');
  }
  const data = await res.json();
  setAccessToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  return data.user;
}

export async function refresh() {
  const rt = getRefreshToken();
  if (!rt) return false;
  const res = await post('/auth/refresh', { refreshToken: rt });
  if (!res.ok) { clearTokens(); return false; }
  const data = await res.json();
  setAccessToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  return true;
}

export async function me() {
  const res = await authedFetch(AUTH_API_URL + '/auth/me');
  if (!res.ok) return null;
  return res.json();
}

export async function verifyOrgCode(organizationCode) {
  const res = await post('/auth/organization-code/verify', { organizationCode });
  if (!res.ok) return { valid: false };
  return res.json();
}

// Always resolves (server responds 202 whether or not the email exists) so the
// UI can't be used to probe which emails have accounts.
export async function forgotPassword(email) {
  await post('/auth/forgot-password', { email });
  return true;
}

export async function resetPassword(token, newPassword) {
  const res = await post('/auth/reset-password', { token, newPassword });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'reset_failed');
  }
  return true;
}

// Caller's org identity + branding + role (from rangedrills-api). Null if unauthenticated.
export async function getOrg() {
  const res = await authedFetch(API_URL + '/api/org');
  if (!res.ok) return null;
  return res.json();
}

// Appends ?orgId=… when a superadmin is targeting another org.
const orgQ = (orgId) => (orgId ? `?orgId=${encodeURIComponent(orgId)}` : '');

// Superadmin-only: every org (for the super-user switcher).
export async function getAllOrgs() {
  const res = await authedFetch(API_URL + '/api/admin/orgs');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error('orgs_load_failed');
  return res.json();
}

// Admin-only: org detail incl. join code. Throws on 403 (non-admin).
export async function getAdminOrg(orgId) {
  const res = await authedFetch(API_URL + '/api/admin/org' + orgQ(orgId));
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error('org_load_failed');
  return res.json();
}

// Admin-only: all members of the target org.
export async function getMembers(orgId) {
  const res = await authedFetch(API_URL + '/api/admin/users' + orgQ(orgId));
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error('members_load_failed');
  return res.json();
}

// Admin-only: mint a new join code (invalidates the old one).
export async function rotateJoinCode(orgId) {
  const res = await authedFetch(API_URL + '/api/admin/org/rotate-code' + orgQ(orgId), { method: 'POST' });
  if (!res.ok) throw new Error('rotate_failed');
  return res.json();
}

// Admin-only: one member's profile.
export async function getMember(userId, orgId) {
  const res = await authedFetch(API_URL + '/api/admin/users/' + encodeURIComponent(userId) + orgQ(orgId));
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) throw new Error('member_load_failed');
  return res.json();
}

// Admin-only: one member's saved session history (with signed photo URLs).
export async function getMemberRoutines(userId, orgId) {
  const res = await authedFetch(API_URL + '/api/admin/users/' + encodeURIComponent(userId) + '/routines' + orgQ(orgId));
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) throw new Error('routines_load_failed');
  return res.json();
}

// Org catalog — the same org-aware drills/routines the org's phone users see.
// (Dual-auth endpoints: the JWT selects the org's merged catalog; superadmin may pass orgId.)
export async function getCatalogDrills(orgId) {
  const res = await authedFetch(API_URL + '/drills' + orgQ(orgId));
  if (!res.ok) throw new Error('drills_load_failed');
  return res.json();
}
export async function getCatalogRoutines(orgId) {
  const res = await authedFetch(API_URL + '/routines' + orgQ(orgId));
  if (!res.ok) throw new Error('routines_load_failed');
  return res.json();
}

// --- Billing (rangedrills-api /api/billing/*) ---

// Start a subscription Checkout for joining the org that `organizationCode`
// resolves to. Returns { id } — the Stripe Checkout Session id the caller hands
// to stripe.redirectToCheckout({ sessionId }) (same pattern as the store).
export async function createMembershipCheckout(organizationCode) {
  const res = await authedFetch(API_URL + '/api/billing/checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ organizationCode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'checkout_failed');
  }
  return res.json();
}

// Open the Stripe Billing Portal (manage/cancel). Returns { url }.
export async function openBillingPortal() {
  const res = await authedFetch(API_URL + '/api/billing/portal-session', { method: 'POST' });
  if (!res.ok) throw new Error('portal_failed');
  return res.json();
}

// Caller's entitlement + current subscription. Null if unauthenticated.
export async function getBillingStatus() {
  const res = await authedFetch(API_URL + '/api/billing/status');
  if (!res.ok) return null;
  return res.json();
}

export async function logout() {
  const rt = getRefreshToken();
  try { await post('/auth/logout', { refreshToken: rt }); } catch (e) {}
  clearTokens();
}

export async function deleteAccount() {
  const res = await authedFetch(AUTH_API_URL + '/auth/account', { method: 'DELETE' });
  clearTokens();
  return res.ok;
}
